import { Dispatch, ThunkAction } from 'redux-thunk';
import getConfig from 'src/config';
import reload from 'src/modules/reload';
import { invalidUserIdToken } from 'src/redux/actions/errors';
import {
  setLoadingCTA,
  setLoadingPage,
  setToSAcceptanceStatus,
  setUserHasBusiness,
} from 'src/redux/actions/options';
import { AppState } from 'src/redux/reducers';
import { AcceptanceApi } from '@jimdo/terms-and-conditions-service-openapi-typescript-node-client';
import { getUserAccountIdFromCookie } from 'src/modules/cookies';
import { trackError } from 'src/modules/tracking';

enum TermsNames {
  ONESTOP = 'ONESTOP',
}

interface CmsBackendWebsiteResponseFromPublicApi {
  id: string;
  name: string | null;
  business_entity?: string;
}

export const grw1410ExperimentId = 'experiment-grw-1410-onestop-product-picker';

export const fetchUserHasBusiness = (): ThunkAction<void, AppState, void> => {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(setLoadingPage(true));
    const userAccountId = getUserAccountIdFromCookie();

    if (userAccountId === '') {
      dispatch(setUserHasBusiness(false));
      return;
    }

    try {
      const response = await fetch(
        `https://register.${
          getConfig().jimdoDomain
        }/api/websites?additional_fields=business_entity&limit=100`
      );
      const json = await response.json();
      const projects: CmsBackendWebsiteResponseFromPublicApi[] = json.results;
      let userHasBusiness = false;
      for (let p of projects) {
        if (p.business_entity !== null) {
          userHasBusiness = true;
          break;
        }
      }
      dispatch(setUserHasBusiness(userHasBusiness));
      dispatch(setLoadingPage(false));
    } catch (e) {
      trackError(e.toString());
      dispatch(setLoadingPage(false));
    }
  };
};

export const acceptOnestopToSAndForward = (): ThunkAction<void, AppState, void> => {
  return async (dispatch: Dispatch<AppState>, getState: () => AppState) => {
    dispatch(setLoadingCTA(true));
    const userAccountId = getUserAccountIdFromCookie();
    const tosAcceptanceStatus = getState().options.onestopToSAccepted;

    if (userAccountId === '') {
      dispatch(setLoadingCTA(false));
      dispatch(invalidUserIdToken());
      setTimeout(reload);
      return;
    }

    if (!tosAcceptanceStatus) {
      const response = await new TermsAndConditionsAcceptanceClient().acceptOneStopTerms(
        userAccountId
      );
      if (!response) {
        dispatch(setLoadingCTA(false));
        return;
      }
    }

    dispatch(setLoadingCTA(false));
    window.location.href = `${getConfig().dashEndpoint}/?founder=true`;
  };
};

export const fetchOnestopToSAcceptanceStatus = (): ThunkAction<void, AppState, void> => {
  return async (dispatch: Dispatch<AppState>) => {
    const userAccountId = getUserAccountIdFromCookie();

    if (userAccountId === '') {
      dispatch(setToSAcceptanceStatus(false));
      return;
    }

    const response = await new TermsAndConditionsAcceptanceClient().checkOneStopTerms(
      userAccountId
    );
    dispatch(setToSAcceptanceStatus(response));
  };
};

export class TermsAndConditionsAcceptanceClient {
  readonly client: AcceptanceApi;
  public constructor() {
    this.client = new AcceptanceApi(`https://register.${getConfig().jimdoDomain}`);
    this.client.defaultHeaders = {
      'Content-type': 'application/json',
      'User-Agent': `jimdo-one-stop/1.0 (app:the-sign-up-flow; component:api; env:${
        getConfig().jimdoEnvironment
      }; +https://jimdo.com)`,
    };
  }

  public async acceptOneStopTerms(userId: string): Promise<boolean> {
    try {
      await this.client.acceptTerms(userId, TermsNames.ONESTOP, {});
      return true;
    } catch (e) {
      trackError(e.toString());
      return false;
    }
  }

  public async checkOneStopTerms(userId: string): Promise<boolean> {
    try {
      await this.client.getLatestTermsAcceptanceForUser(userId, TermsNames.ONESTOP);
      return true;
    } catch (e) {
      trackError(e.toString());
      return false;
    }
  }
}
